import { formatDate } from '@/lib/date-time';
/**
 * @file routes/_app/certificates/$id.tsx
 * @description 驗證證書詳情頁（M3）。
 *
 * 功能：
 * - Header：certNo、clientName、status、issuedAt、expiresAt
 * - 版本歷史（Collapse）：version、issuedAt、validFrom、validUntil
 * - 最新版本 productScope（正規化表格展示：產品品項/面積，兼容舊 string[] 資料）
 * - 最新版本寄送記錄列表（記錄寄送 / 確認簽收皆為頁內彈窗，無獨立寄送頁）
 * - 操作按鈕：
 *   - 下載可编辑 DOCX（首頁 + 附表 + 註解／變更註記）
 *   - 下載 PDF → window.open('/api/certificates/:id/print.pdf')
 *   - 記錄寄送 → 頁內 Modal 表單（需 certificate.issue 能力）
 *
 * 視覺：PageShell + SectionCard + DataField
 *
 * 角色：所有登入角色可查看；寄送管理需 admin/manager
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  DatePicker,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import { ArrowLeft, Award, CheckCircle, Download, FileText, Send } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  CERT_TEMPLATES,
  CERT_TYPE_LABELS,
  userCan,
  type CertType,
  type CertAnnexItem,
} from '@zhcb/shared';
import { message, modal } from '@/lib/antd-app';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import {
  PageShell,
  SectionCard,
  DataField,
  DataFieldGrid,
  StatusPill,
  PageMetaItem,
} from '@/components/shell';
import { CertChangesCard } from '@/components/certificates/CertChangesCard';
import { color, space } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/certificates/$id')({
  component: CertificateDetailPage,
});

// ─── 類型定義 ─────────────────────────────────────────────────────────────────

interface CertVersion {
  id: number;
  version: number;
  productScope: unknown;
  validFrom: string | null;
  validUntil: string | null;
  issuedAt: string | null;
  issuedBy: number;
  revokedAt?: string | null;
  revokedReason?: string | null;
}

interface DeliveryRecord {
  id: number;
  deliveryMethod: string;
  trackingNumber: string | null;
  recipientName: string | null;
  shippedAt: string | null;
  receivedAt: string | null;
  isCompleted: boolean;
  notes: string | null;
  createdAt: string;
}

/** 證書附表（序 38） */
interface CertAnnex {
  items: CertAnnexItem[];
  remark?: string;
  confirmed: boolean;
  confirmedBy?: number;
  confirmedAt?: string;
}

interface CertDetail {
  id: number;
  /** 歷史欄名，實際為客戶驗證註冊碼。 */
  certNo: string;
  /** 按 TOP/TAP 規則生成的正式證書字號；存量資料可能為 null。 */
  certDocNo: string | null;
  clientId: number;
  clientName: string;
  caseId: number;
  status: string;
  certType: CertType | null;
  certAnnex: CertAnnex | null;
  registrationDate: string | null;
  documentRemark: string | null;
  changeNote: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  currentVersionId: number | null;
  createdAt: string;
  versions: CertVersion[];
  deliveries: DeliveryRecord[];
}

// ─── 狀態顏色 ─────────────────────────────────────────────────────────────��──

const STATUS_TONE: Record<string, 'info' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  製證中: 'info',
  待寄送: 'info',
  已寄送: 'brand' as never,
  有效: 'success',
  即將到期: 'warning',
  已到期: 'danger',
  暫停: 'warning',
  撤銷: 'neutral',
  終止: 'neutral',
};

const DELIVERY_METHOD_LABEL: Record<string, string> = {
  registered_mail: '掛號郵件',
  courier: '快遞',
  pickup: '自取',
};

// ─── 產品範圍正規化 ──────────────────────────────────────────────────────────

/** 產品範圍展示行（正規化後） */
interface ProductScopeRow {
  /** 產品品項 / 驗證範圍描述 */
  product: string;
  /** 面積（可空） */
  area?: string;
  /** 單位（可空） */
  unit?: string;
}

/**
 * 把 certificateVersions.productScope（jsonb，歷史上存在多種形態）正規化為表格行：
 * - 正規形：`[{ product, area?, unit? }]`（issueCertificateSchema）
 * - 舊 seed 資料：`string[]`
 * - 其他未知形態：整項 JSON 字串化兜底，保證不再對用戶裸露 raw JSON dump
 */
function normalizeProductScope(raw: unknown): ProductScopeRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry): ProductScopeRow => {
    if (typeof entry === 'string') return { product: entry };
    if (entry && typeof entry === 'object') {
      const obj = entry as Record<string, unknown>;
      if (typeof obj['product'] === 'string') {
        return {
          product: obj['product'],
          area: typeof obj['area'] === 'string' ? obj['area'] : undefined,
          unit: typeof obj['unit'] === 'string' ? obj['unit'] : undefined,
        };
      }
    }
    return { product: JSON.stringify(entry) };
  });
}

/** 記錄寄送 Modal 表單值 */
interface DeliveryFormValues {
  deliveryMethod: 'registered_mail' | 'courier' | 'pickup';
  trackingNumber?: string;
  shippedAt?: Dayjs;
  recipientName?: string;
  notes?: string;
}

interface DocumentFormValues {
  certDocNo: string;
  registrationDate: Dayjs;
  remark?: string;
  changeNote?: string;
  certType: CertType;
}

// ─── 組件 ────────────────────────────────────────────────────────────────────

/**
 * 驗證證書詳情頁主組件。
 */
function CertificateDetailPage() {
  const { id } = Route.useParams();
  const certId = Number(id);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user!);
  const canIssue = userCan(user, 'certificate.issue');
  const queryClient = useQueryClient();

  // 證書附表編輯 Modal 狀態
  const [annexOpen, setAnnexOpen] = useState(false);
  const [annexForm] = Form.useForm<{ items: CertAnnexItem[]; remark?: string }>();

  // 記錄寄送 Modal 狀態（原獨立 /certificates/:id/delivery 頁已併入本頁彈窗）
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [deliveryForm] = Form.useForm<DeliveryFormValues>();
  const [documentOpen, setDocumentOpen] = useState(false);
  const [documentForm] = Form.useForm<DocumentFormValues>();

  // ─── 查詢證書詳情 ────────────────────────────────────────────────
  const { data, isLoading } = useQuery<{ item: CertDetail }>({
    queryKey: ['certificate-detail', certId],
    queryFn: () => apiClient.get(`/api/certificates/${certId}`),
  });

  const cert = data?.item;

  // ─── 開啟附表編輯：已有附表帶入，否則拉預填草稿 ───────────────────
  const openAnnexEditor = async () => {
    try {
      if (cert?.certAnnex?.items?.length) {
        annexForm.setFieldsValue({
          items: cert.certAnnex.items,
          remark: cert.certAnnex.remark,
        });
      } else {
        const res = await apiClient.get<{
          item: { items: CertAnnexItem[]; remark?: string };
        }>(`/api/certificates/${certId}/annex-draft`);
        annexForm.setFieldsValue({
          items: res.item.items?.length ? res.item.items : [{ product: '' }],
          remark: res.item.remark,
        });
      }
      setAnnexOpen(true);
    } catch (err) {
      // 前端異常提示
      message.error('載入證書附表預填失敗');
      console.error('load cert annex draft failed', err);
    }
  };

  // ─── 儲存 / 確認附表 mutation ─────────────────────────────────────
  const annexMutation = useMutation({
    mutationFn: (payload: { items: CertAnnexItem[]; remark?: string; confirm: boolean }) =>
      apiClient.patch(`/api/certificates/${certId}/annex`, payload),
    onSuccess: (_data, vars) => {
      message.success(vars.confirm ? '證書附表已確認，將反饋管理組' : '證書附表已儲存');
      setAnnexOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['certificate-detail', certId] });
    },
    onError: (err) => {
      message.error('證書附表儲存失敗');
      console.error('save cert annex failed', err);
    },
  });

  /**
   * 送出附表（confirm=true 表示確認反饋管理組）。
   * 2026-09-25 回報序49：「儲存草稿」隨時可存——不跑必填校驗，只略過完全空白的列；
   * 「確認並反饋管理組」才做完整校驗（每列須填品項、至少一列）。
   */
  const submitAnnex = async (confirm: boolean) => {
    if (confirm) {
      const values = await annexForm.validateFields();
      annexMutation.mutate({ items: values.items, remark: values.remark, confirm });
      return;
    }
    const raw = annexForm.getFieldsValue(true) as { items?: CertAnnexItem[]; remark?: string };
    // 草稿：品項可暫缺（後端草稿不要求品項），僅略過完全空白的列
    const items = (raw.items ?? [])
      .filter((it) =>
        [it?.product, it?.category, it?.site, it?.area, it?.note].some((v) => !!v?.trim()),
      )
      .map((it) => ({ ...it, product: it.product?.trim() ?? '' }));
    annexMutation.mutate({ items, remark: raw.remark, confirm: false });
  };

  // ─── 記錄寄送 mutation ────────────────────────────────────────────
  const deliveryMutation = useMutation({
    mutationFn: (body: {
      deliveryMethod: string;
      trackingNumber?: string;
      shippedAt?: string;
      recipientName?: string;
      notes?: string;
    }) => apiClient.post(`/api/certificates/${certId}/delivery`, body),
    onSuccess: () => {
      message.success('寄送記錄已建立');
      setDeliveryOpen(false);
      deliveryForm.resetFields();
      void queryClient.invalidateQueries({ queryKey: ['certificate-detail', certId] });
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '記錄寄送失敗');
      console.error('create cert delivery failed', err);
    },
  });

  // ─── 確認簽收 mutation ────────────────────────────────────────────
  const confirmDeliveryMutation = useMutation({
    mutationFn: () => apiClient.patch(`/api/certificates/${certId}/delivery/confirm`, {}),
    onSuccess: () => {
      message.success('已確認收到，證書狀態更新為「有效」');
      void queryClient.invalidateQueries({ queryKey: ['certificate-detail', certId] });
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '確認簽收失敗');
      console.error('confirm cert delivery failed', err);
    },
  });

  const documentMutation = useMutation({
    mutationFn: (values: DocumentFormValues) =>
      apiClient.patch(`/api/certificates/${certId}/document`, {
        ...values,
        registrationDate: values.registrationDate.format('YYYY-MM-DD'),
      }),
    onSuccess: () => {
      message.success('製證資料已更新');
      setDocumentOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['certificate-detail', certId] });
    },
    onError: (err) => {
      console.error('update certificate document failed', err);
      message.error(err instanceof Error ? err.message : '製證資料更新失敗');
    },
  });

  if (isLoading) return <div className="p-6">載入中...</div>;
  if (!cert) {
    return (
      <div className="p-6">
        <Alert type="error" message="證書不存在或無法存取" />
      </div>
    );
  }

  const latestVersion = cert.versions?.[0] ?? null;

  /** 最新版本產品範圍（正規化為表格行，兼容 string[] 舊資料） */
  const productScopeRows = normalizeProductScope(latestVersion?.productScope);

  /** 進行中的寄送記錄（有則顯示「確認簽收」入口） */
  const pendingDelivery = cert.deliveries?.find((d) => !d.isCompleted) ?? null;

  /** 寄送記錄表格列 */
  const deliveryColumns: ColumnsType<DeliveryRecord> = [
    {
      title: '寄送方式',
      dataIndex: 'deliveryMethod',
      width: 100,
      render: (m: string) => DELIVERY_METHOD_LABEL[m] ?? m,
    },
    {
      title: '追蹤號碼',
      dataIndex: 'trackingNumber',
      width: 150,
      render: (v: string | null) => v ?? '—',
    },
    { title: '收件人', dataIndex: 'recipientName', render: (v: string | null) => v ?? '—' },
    {
      title: '寄送時間',
      dataIndex: 'shippedAt',
      width: 120,
      render: (d: string | null) => (d ? formatDate(d) : '—'),
    },
    {
      title: '簽收時間',
      dataIndex: 'receivedAt',
      width: 120,
      render: (d: string | null) => (d ? formatDate(d) : '—'),
    },
    {
      title: '狀態',
      dataIndex: 'isCompleted',
      width: 80,
      render: (v: boolean) => <Tag color={v ? 'green' : 'orange'}>{v ? '已完成' : '進行中'}</Tag>,
    },
  ];

  return (
    <PageShell
      title={`證書詳情 — ${cert.certDocNo ?? cert.certNo}`}
      meta={
        <>
          <StatusPill tone={STATUS_TONE[cert.status] ?? 'neutral'} size="sm">
            {cert.status}
          </StatusPill>
          <PageMetaItem label="客戶" value={cert.clientName} />
        </>
      }
      actions={
        <Space>
          <Button
            icon={<ArrowLeft size={14} />}
            onClick={() => void navigate({ to: '/certificates' })}
          >
            返回列表
          </Button>
          <Button
            icon={<FileText size={14} />}
            onClick={() => window.open(`/api/certificates/${certId}/print.docx`, '_blank')}
          >
            下載 DOCX
          </Button>
          <Button
            icon={<Download size={14} />}
            onClick={() => window.open(`/api/certificates/${certId}/print.pdf`, '_blank')}
          >
            下載 PDF
          </Button>
          {canIssue && (
            <>
              <Button
                onClick={() => {
                  documentForm.setFieldsValue({
                    certDocNo: cert.certDocNo ?? cert.certNo,
                    registrationDate: dayjs(cert.registrationDate ?? cert.issuedAt ?? undefined),
                    remark: cert.documentRemark ?? cert.certAnnex?.remark,
                    changeNote: cert.changeNote ?? undefined,
                    certType: cert.certType ?? 'organic_individual',
                  });
                  setDocumentOpen(true);
                }}
              >
                編輯製證資料
              </Button>
              <Button
                type="primary"
                icon={<Send size={14} />}
                onClick={() => setDeliveryOpen(true)}
              >
                記錄寄送
              </Button>
            </>
          )}
        </Space>
      }
    >
      {/* ── 基本資訊 ── */}
      <div style={{ marginBottom: space[4] }}>
        <SectionCard icon={<Award size={14} />} title="基本資訊">
          <DataFieldGrid columns={3}>
            <DataField label="證書字號" value={cert.certDocNo ?? cert.certNo} mono />
            <DataField label="驗證註冊碼" value={cert.certNo} mono />
            <DataField label="客戶名稱" value={cert.clientName} />
            <DataField
              label="證書類型"
              value={cert.certType ? CERT_TYPE_LABELS[cert.certType] : '—'}
            />
            <DataField
              label="狀態"
              value={
                <StatusPill tone={STATUS_TONE[cert.status] ?? 'neutral'} size="sm">
                  {cert.status}
                </StatusPill>
              }
            />
            <DataField
              label="核發日期"
              value={cert.issuedAt ? formatDate(cert.issuedAt) : '—'}
            />
            <DataField
              label="到期日"
              value={cert.expiresAt ? formatDate(cert.expiresAt) : '—'}
            />
          </DataFieldGrid>
        </SectionCard>
      </div>

      {/* ── 版本歷史 ── */}
      {cert.versions && cert.versions.length > 0 && (
        <div style={{ marginBottom: space[4] }}>
          <SectionCard title="版本歷史" padless>
            <div style={{ padding: space[4] }}>
              <Collapse
                defaultActiveKey={cert.versions[0]?.id ? [String(cert.versions[0].id)] : []}
                items={cert.versions.map((v) => ({
                  key: String(v.id),
                  label: (
                    <span>
                      版本 {v.version}
                      {cert.currentVersionId === v.id && (
                        <Tag color="green" className="ml-2">
                          最新
                        </Tag>
                      )}
                      <span style={{ marginLeft: 8, color: color.text.muted, fontSize: 12 }}>
                        {v.validFrom ? formatDate(v.validFrom) : '?'} ~{' '}
                        {v.validUntil ? formatDate(v.validUntil) : '?'}
                      </span>
                    </span>
                  ),
                  children: (
                    <DataFieldGrid columns={2}>
                      <DataField
                        label="核發日期"
                        value={v.issuedAt ? formatDate(v.issuedAt) : '—'}
                      />
                      <DataField
                        label="有效期間"
                        value={`${v.validFrom ? formatDate(v.validFrom) : '—'} ～ ${v.validUntil ? formatDate(v.validUntil) : '—'}`}
                      />
                      {v.revokedAt && (
                        <DataField
                          label="撤銷時間"
                          value={formatDate(v.revokedAt)}
                        />
                      )}
                      {v.revokedReason && <DataField label="撤銷原因" value={v.revokedReason} />}
                    </DataFieldGrid>
                  ),
                }))}
              />
            </div>
          </SectionCard>
        </div>
      )}

      {/* ── 產品範圍（最新版本）：正規化後表格展示，不裸露 raw JSON ── */}
      {productScopeRows.length > 0 && (
        <div style={{ marginBottom: space[4] }}>
          <SectionCard title="產品範圍（最新版本）" padless>
            <Table<ProductScopeRow>
              dataSource={productScopeRows}
              rowKey={(_r, i) => String(i)}
              size="small"
              pagination={false}
              columns={[
                { title: '產品品項', dataIndex: 'product' },
                // 面積欄僅在至少一行帶面積時顯示，避免整欄「—」
                ...(productScopeRows.some((r) => r.area)
                  ? [
                      {
                        title: '面積',
                        dataIndex: 'area',
                        width: 200,
                        render: (_: unknown, r: ProductScopeRow) =>
                          r.area ? `${r.area}${r.unit ? ` ${r.unit}` : ''}` : '—',
                      },
                    ]
                  : []),
              ]}
            />
          </SectionCard>
        </div>
      )}

      {/* ── 證書附表（序 38） ── */}
      <div style={{ marginBottom: space[4] }}>
        <SectionCard
          icon={<FileText size={14} />}
          title="證書附表"
          padless
          actions={
            canIssue && (
              <Button
                type="primary"
                size="small"
                icon={<FileText size={12} />}
                onClick={() => void openAnnexEditor()}
              >
                {cert.certAnnex?.items?.length ? '編輯 / 確認附表' : '建立附表'}
              </Button>
            )
          }
        >
          <div style={{ padding: space[4] }}>
            {cert.certAnnex?.confirmed && (
              <Alert
                type="success"
                showIcon
                style={{ marginBottom: space[3] }}
                message={`附表已確認${cert.certAnnex.confirmedAt ? `（${formatDate(cert.certAnnex.confirmedAt)}）` : ''}，已連同稽核文件反饋管理組`}
              />
            )}
            {cert.certAnnex?.items?.length ? (
              <Table<CertAnnexItem>
                dataSource={cert.certAnnex.items}
                rowKey={(_r, i) => String(i)}
                size="small"
                pagination={false}
                columns={[
                  { title: '產品品項', dataIndex: 'product' },
                  {
                    title: '產品類別',
                    dataIndex: 'category',
                    render: (v: string | undefined) => v ?? '—',
                  },
                  {
                    title: '場所 / 生產地',
                    dataIndex: 'site',
                    render: (v: string | undefined) => v ?? '—',
                  },
                  {
                    title: '面積（公頃）',
                    dataIndex: 'area',
                    width: 110,
                    render: (v: string | undefined) => v ?? '—',
                  },
                  {
                    title: '備註',
                    dataIndex: 'note',
                    render: (v: string | undefined) => v ?? '—',
                  },
                ]}
              />
            ) : (
              <div style={{ textAlign: 'center', color: color.text.tertiary, padding: 12 }}>
                尚未建立證書附表（檢驗報告判定通過 / 稽核結案時確認）
              </div>
            )}
            {cert.certAnnex?.remark && (
              <div style={{ marginTop: space[3], color: color.text.muted, fontSize: 13 }}>
                備註：{cert.certAnnex.remark}
              </div>
            )}
          </div>
        </SectionCard>
      </div>

      {/* ── 證書變更（序93：ZHSR06-1 申請 → 客戶簽名 → ZHSR06-2 評審 → 歷程） ── */}
      <div style={{ marginBottom: space[4] }}>
        <CertChangesCard certificateId={Number(id)} canManage={canIssue} />
      </div>

      {/* ── 寄送記錄 ── */}
      <SectionCard
        title={`寄送記錄（${cert.deliveries?.length ?? 0} 筆）`}
        padless
        actions={
          canIssue && (
            <Space>
              {pendingDelivery && (
                <Button
                  size="small"
                  icon={<CheckCircle size={12} />}
                  loading={confirmDeliveryMutation.isPending}
                  onClick={() => {
                    modal.confirm({
                      title: '確認客戶已收到證書？',
                      content: '確認後，證書狀態將更新為「有效」，案件將進入「結案」流程。',
                      okText: '確認簽收',
                      cancelText: '取消',
                      onOk: () => {
                        confirmDeliveryMutation.mutate();
                      },
                    });
                  }}
                >
                  確認簽收
                </Button>
              )}
              <Button
                type="primary"
                size="small"
                icon={<Send size={12} />}
                onClick={() => setDeliveryOpen(true)}
              >
                記錄寄送
              </Button>
            </Space>
          )
        }
      >
        {!cert.deliveries || cert.deliveries.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: color.text.tertiary }}>
            尚無寄送記錄
          </div>
        ) : (
          <Table<DeliveryRecord>
            dataSource={cert.deliveries}
            rowKey="id"
            columns={deliveryColumns}
            size="small"
            pagination={false}
          />
        )}
      </SectionCard>

      {/* ── 證書附表編輯 Modal（序 38） ── */}
      <Modal
        title="編輯製證資料"
        open={documentOpen}
        okText="儲存"
        cancelText="取消"
        confirmLoading={documentMutation.isPending}
        onCancel={() => setDocumentOpen(false)}
        onOk={() =>
          void documentForm.validateFields().then((values) => documentMutation.mutate(values))
        }
      >
        <Form form={documentForm} layout="vertical">
          <Form.Item
            name="certDocNo"
            label="證書字號"
            rules={[{ required: true, message: '請填寫證書字號' }]}
          >
            <Input maxLength={30} />
          </Form.Item>
          <Form.Item
            name="certType"
            label="證書範本"
            rules={[{ required: true, message: '請選擇證書範本' }]}
          >
            <Select
              options={CERT_TEMPLATES.map((template) => ({
                value: template.type,
                label: template.label,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="registrationDate"
            label="登記日期"
            rules={[{ required: true, message: '請選擇登記日期' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remark" label="首頁／附表備註">
            <Input.TextArea rows={2} maxLength={2000} showCount />
          </Form.Item>
          <Form.Item name="changeNote" label="驗證事項變更註記／補充註記">
            <Input.TextArea rows={3} maxLength={2000} showCount placeholder="無變更時填「無」" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── 證書附表編輯 Modal（序 38） ── */}
      <Modal
        title="證書附表"
        open={annexOpen}
        onCancel={() => setAnnexOpen(false)}
        width={760}
        footer={[
          <Button key="cancel" onClick={() => setAnnexOpen(false)}>
            取消
          </Button>,
          <Button
            key="save"
            loading={annexMutation.isPending}
            onClick={() => void submitAnnex(false)}
          >
            儲存草稿
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={annexMutation.isPending}
            onClick={() => void submitAnnex(true)}
          >
            確認並反饋管理組
          </Button>,
        ]}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: space[3] }}
          message="檢驗報告判定通過後 / 稽核結案時確認證書附表，確認後連同其他稽核文件反饋管理組。"
        />
        <Form form={annexForm} layout="vertical">
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {fields.map((field) => (
                  <Space key={field.key} align="baseline" wrap>
                    <Form.Item
                      name={[field.name, 'product']}
                      rules={[{ required: true, message: '請填品項' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input placeholder="產品品項" style={{ width: 150 }} />
                    </Form.Item>
                    <Form.Item name={[field.name, 'category']} style={{ marginBottom: 0 }}>
                      <Input placeholder="產品類別" style={{ width: 110 }} />
                    </Form.Item>
                    <Form.Item name={[field.name, 'site']} style={{ marginBottom: 0 }}>
                      <Input placeholder="場所 / 生產地" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item name={[field.name, 'area']} style={{ marginBottom: 0 }}>
                      <Input placeholder="面積(公頃)" style={{ width: 100 }} />
                    </Form.Item>
                    <Form.Item name={[field.name, 'note']} style={{ marginBottom: 0 }}>
                      <Input placeholder="備註" style={{ width: 120 }} />
                    </Form.Item>
                    <Button danger size="small" onClick={() => remove(field.name)}>
                      刪除
                    </Button>
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add({ product: '' })}>
                  + 新增一列
                </Button>
              </div>
            )}
          </Form.List>
          <Form.Item name="remark" label="附表備註" style={{ marginTop: 12 }}>
            <Input.TextArea rows={2} placeholder="附表整體備註（可選）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── 記錄寄送 Modal（原獨立寄送頁併入） ── */}
      <Modal
        title="記錄寄送"
        open={deliveryOpen}
        onCancel={() => setDeliveryOpen(false)}
        okText="記錄寄送"
        cancelText="取消"
        confirmLoading={deliveryMutation.isPending}
        onOk={() => deliveryForm.submit()}
        destroyOnHidden
      >
        {pendingDelivery && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: space[3] }}
            message="已有進行中的寄送記錄"
            description={`寄送方式：${DELIVERY_METHOD_LABEL[pendingDelivery.deliveryMethod] ?? pendingDelivery.deliveryMethod}｜追蹤號碼：${pendingDelivery.trackingNumber ?? '未填寫'}｜收件人：${pendingDelivery.recipientName ?? '未填寫'}`}
          />
        )}
        <Form
          form={deliveryForm}
          layout="vertical"
          onFinish={(values: DeliveryFormValues) => {
            deliveryMutation.mutate({
              deliveryMethod: values.deliveryMethod,
              trackingNumber: values.trackingNumber,
              shippedAt: values.shippedAt?.toISOString(),
              recipientName: values.recipientName,
              notes: values.notes,
            });
          }}
        >
          <Form.Item
            name="deliveryMethod"
            label="寄送方式"
            rules={[{ required: true, message: '請選擇寄送方式' }]}
          >
            <Radio.Group
              options={[
                { value: 'registered_mail', label: '掛號郵件' },
                { value: 'courier', label: '快遞' },
                { value: 'pickup', label: '自取' },
              ]}
            />
          </Form.Item>
          <Form.Item name="trackingNumber" label="追蹤號碼（選填）">
            <Input placeholder="例：RR123456789TW" maxLength={100} />
          </Form.Item>
          <Form.Item name="recipientName" label="收件人（選填）">
            <Input placeholder="收件人姓名" maxLength={100} />
          </Form.Item>
          <Form.Item name="shippedAt" label="寄出日期（選填）">
            <DatePicker format="YYYY/MM/DD" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="備注（選填）">
            <Input.TextArea rows={3} maxLength={500} placeholder="其他備注..." />
          </Form.Item>
        </Form>
      </Modal>
    </PageShell>
  );
}
